import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../../config/config';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellularConnectedNoInternet4BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet4Bar';

const WaterLevelFunctionalSettingsDialog = ({ open, onClose, sensorsData, deviceCode, subId }) => {
  console.log(sensorsData);
  
  // Initialize state for functional points
  const [functionalPoints, setFunctionalPoints] = useState({
    veryLow: '',
    lowLevel: '',
    highLevel: '',
    veryHighLevel: '',
    positiveFlowHigh: '',
    negativeFlowHigh: '',
    positiveFlowLow: '',
    negativeFlowLow: ''
  });

  // State for Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' | 'error' | 'warning' | 'info'
  });

  // State for loading indicator
  const [loading, setLoading] = useState(false);

  // Extract networkStrength and batteryVoltage
  const [networkStrength, setNetworkStrength] = useState(null);
  const [batteryVoltage, setBatteryVoltage] = useState(null);

  // Effect to load functional points and indicators when dialog opens or sensorsData changes
  useEffect(() => {
    if (open && sensorsData.length > 0) {
      const sensor = sensorsData[0]; // Since sensorsData is an array with one sensor
      if (sensor && sensor.waterLevelSensorData) {
        // Extract functional points
        if (sensor.waterLevelSensorData.functionalPoints) {
          setFunctionalPoints({
            veryLow: sensor.waterLevelSensorData.functionalPoints.veryLow || 0,
            lowLevel: sensor.waterLevelSensorData.functionalPoints.lowLevel || 0,
            highLevel: sensor.waterLevelSensorData.functionalPoints.highLevel || 0,
            veryHighLevel: sensor.waterLevelSensorData.functionalPoints.veryHighLevel || 0,
            positiveFlowHigh: sensor.waterLevelSensorData.functionalPoints.positiveFlowHigh || 0,
            negativeFlowHigh: sensor.waterLevelSensorData.functionalPoints.negativeFlowHigh || 0,
            positiveFlowLow: sensor.waterLevelSensorData.functionalPoints.positiveFlowLow || 0,
            negativeFlowLow: sensor.waterLevelSensorData.functionalPoints.negativeFlowLow || 0
          });
        } else {
          // If functionalPoints data is not found, reset the form
          setFunctionalPoints({
            veryLow: '',
            lowLevel: '',
            highLevel: '',
            veryHighLevel: '',
            positiveFlowHigh: '',
            negativeFlowHigh: '',
            positiveFlowLow: '',
            negativeFlowLow: ''
          });
          setSnackbar({
            open: true,
            message: 'Functional points data not found for the selected sensor.',
            severity: 'error'
          });
        }

        // Extract networkStrength and batteryVoltage
        if (sensor.waterLevelSensorData.errors) {
          setNetworkStrength(sensor.waterLevelSensorData.errors.networkStrength);
          setBatteryVoltage(sensor.waterLevelSensorData.errors.batteryVoltage);
        } else {
          setNetworkStrength(null);
          setBatteryVoltage(null);
        }
      } else {
        // If sensor or waterLevelSensorData is not found, reset the form and indicators
        setFunctionalPoints({
          veryLow: '',
          lowLevel: '',
          highLevel: '',
          veryHighLevel: '',
          positiveFlowHigh: '',
          negativeFlowHigh: '',
          positiveFlowLow: '',
          negativeFlowLow: ''
        });
        setNetworkStrength(null);
        setBatteryVoltage(null);
        setSnackbar({
          open: true,
          message: 'Sensor data not found for the selected sensor.',
          severity: 'error'
        });
      }
    }
  }, [open, sensorsData]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFunctionalPoints((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate inputs (ensure all are numbers)
    for (const [key, value] of Object.entries(functionalPoints)) {
      if (value === '' || isNaN(Number(value))) {
        setSnackbar({
          open: true,
          message: `Please enter a valid number for ${formatLabel(key)}.`,
          severity: 'error'
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare payload with only the functional points
      const payload = { ...functionalPoints };

      // Make API request to update functional points
      const response = await axios.patch(
        `${API_BASE_URL}api/sensor/waterlevelsensor/${deviceCode}/${subId}`,
        payload
      );

      // Handle success
      setSnackbar({
        open: true,
        message: 'Functional points updated successfully.',
        severity: 'success'
      });

      // Close the dialog after a short delay to allow users to see the success message
      setTimeout(() => {
        onClose();
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error updating functional points:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'An error occurred while updating functional points.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Format label from camelCase to Title Case with spaces
  const formatLabel = (label) => {
    return label
      .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize the first letter
      .trim();
  };

  // Handle dialog close (reset state if necessary)
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Helper function to get network strength icon
  const getNetworkIcon = (strength) => {
    if (strength > 75) return <SignalCellular4BarIcon color="success" />;
    if (strength > 50) return <SignalCellular3BarIcon color="info" />;
    if (strength > 25) return <SignalCellular2BarIcon color="warning" />;
    if (strength > 0) return <SignalCellular1BarIcon color="error" />;
    return <SignalCellularConnectedNoInternet4BarIcon color="disabled" />;
  };

  // Helper function to get battery voltage icon
  const getBatteryIcon = (voltage) => {
    if (voltage > 3.75) return <BatteryFullIcon color="success" />;
    if (voltage > 3.50) return <BatteryChargingFullIcon color="info" />;
    if (voltage > 2.50) return <BatteryChargingFullIcon color="warning" />;
    if (voltage > 0) return <BatteryChargingFullIcon color="error" />;
    return <BatteryFullIcon color="disabled" />;
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        {/* Stepper-style header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', borderRadius: 2, mb: 2 }}>
          <BatteryFullIcon sx={{ color: '#1976d2', fontSize: 28, mx: 1 }} />
          <Typography variant="h6" fontWeight={700} color="#1976d2">Functional Settings</Typography>
          <SignalCellular4BarIcon sx={{ color: '#43a047', fontSize: 28, mx: 1 }} />
        </Box>
        <DialogContent sx={{ background: 'linear-gradient(90deg, #f8fafc 0%, #e3f2fd 100%)', borderRadius: 2 }}>
          {/* Network and Battery Indicators in card */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: '0 2px 8px #e3f2fd', background: '#fff' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  {getNetworkIcon(networkStrength)}
                  <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#1976d2' }}>
                    Network: {networkStrength !== null ? `-${Math.abs(networkStrength)} dBm` : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  {getBatteryIcon(batteryVoltage)}
                  <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#43a047' }}>
                    Battery: {batteryVoltage !== null ? `${batteryVoltage}V` : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          {/* Functional Points in colored cards */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(functionalPoints).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', boxShadow: '0 1px 4px #e3f2fd' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#1976d2' }}>{formatLabel(key)}</Typography>
                  <TextField
                    name={key}
                    type="number"
                    value={value}
                    onChange={handleChange}
                    fullWidth
                    variant="filled"
                    InputProps={{ inputProps: { min: 0 }, sx: { borderRadius: 10, background: '#f0f2f5' } }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2, justifyContent: 'center' }}>
          <Button onClick={handleClose} sx={{ borderRadius: 20, px: 4 }} color="secondary" disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 20, px: 4, boxShadow: '0 2px 8px #1976d2' }}
            disabled={loading || functionalPoints.veryLow === ''}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Define PropTypes for type checking
WaterLevelFunctionalSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sensorsData: PropTypes.arrayOf(
    PropTypes.shape({
      device_code: PropTypes.string.isRequired,
      sensor_sub_id: PropTypes.string.isRequired,
      waterLevelSensorData: PropTypes.shape({
        functionalPoints: PropTypes.shape({
          veryLow: PropTypes.number,
          lowLevel: PropTypes.number,
          highLevel: PropTypes.number,
          veryHighLevel: PropTypes.number,
          positiveFlowHigh: PropTypes.number,
          negativeFlowHigh: PropTypes.number,
          positiveFlowLow: PropTypes.number,
          negativeFlowLow: PropTypes.number
        }),
        errors: PropTypes.shape({
          networkStrength: PropTypes.number,
          batteryVoltage: PropTypes.number
        })
      })
    })
  ).isRequired,
  deviceCode: PropTypes.string.isRequired,
  subId: PropTypes.string.isRequired
};

export default WaterLevelFunctionalSettingsDialog;
